import React, { useState, useEffect, useMemo } from 'react';

import GroupUserService from 'services/groupUser.service';

import { IDomainSetting } from 'models/DomainSetting';

import IconSuccess from 'img/success-img.svg';
import Text from 'components/Text';
import { Row, Column } from 'components/Layout/Grid';
import { Button } from 'components/Button';
import { IUser } from 'models/User';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/ducks';
import Form from 'components/Form';
import Input from 'components/Input';
import AutoComplete from 'components/AutoComplete';
import AutoCompleteKeycloakUserModel from 'components/AutoComplete/KeycloakUser';
import Validator from 'components/Validator';
import AutoCompleteSellerModel from 'components/AutoComplete/Seller';
import Loading from 'components/Loading';
import AutoCompleteManagerModel from 'components/AutoComplete/Manager';
import toast from 'components/Toast/dispatch';
import SwitchComponent from 'components/SwitchComponent';
import { Visibility } from 'components/Visibility';

const AddEditEmployee: React.FC = () => {
  const slider = useSelector((state: RootState) => state.slider);
  const sliderUser = slider?.state?.userProp;
  const user = useSelector((state: { user: IUser; }) => state.user);
  const administrator = user?.roles?.includes('administrator') ?? '';
  const novo = sliderUser === undefined;

  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [newUserData, setNewUserData] = useState({
    id: sliderUser?.id,
    managerId: sliderUser?.manager?.id,
    managerEmail: sliderUser?.manager?.email,
    manager: sliderUser?.manager,
    fullName: sliderUser?.fullName,
    email: sliderUser?.email,
    roles: novo ? ['administrator'] : sliderUser?.roles,
    profileType: novo ? [] : sliderUser?.profileType,
    whoInvited: sliderUser?.whoInvited,
    lastAccess: sliderUser?.lastAccess,
    expirationDate: sliderUser?.expirationDate,
    userType: sliderUser?.userType,
    enabled: sliderUser?.enabled,
    username: sliderUser?.username,
    customerId: sliderUser?.customerId,
    sellerId: sliderUser?.seller?.id,
    seller: sliderUser?.seller,
  });

  const [autoCompleteSelected, setAutoCompleteSelected] = useState(novo ? false : true);

  const dispatch = useDispatch();
  const _groupUserService = new GroupUserService();

  const [showAutoComplete, setShowAutoComplete] = useState(false);
  const [showAutoCompleteSeller, setShowAutoCompleteSeller] = useState(false);
  const [showAutoCompleteManager, setShowAutoCompleteManager] = useState(false);

  const [userProfileTypes, setUserProfileTypes] = useState(Array<IDomainSetting>());

  const loadedSliderData = useMemo(() => userProfileTypes.length > 0, [userProfileTypes]);

  const disabledUserName = administrator ? false : !novo;
  const textButtonSave = novo ? 'Vincular' : 'Alterar';
  const textSuccess = novo ? 'Cadastrado' : 'Alterado';

  useEffect(() => {
    _groupUserService
      .getProfiles()
      .then((result: any) => {
        if (result && result.data) {
          setUserProfileTypes(result.data);
        }
      });
  }, []);

  function close() {
    slider?.state?.getUsers();
    dispatch({ type: 'CLOSE_SLIDER' });
    dispatch({ type: 'SHOW_TITLE' });
  }

  async function registerUser() {
    setLoading(true);
    setErrorMessage('');

    if (!autoCompleteSelected) {
      setLoading(false);
      return setErrorMessage('Usuário de Rede inválido');
    }

    if (newUserData.profileType?.length === 0) {
      setLoading(false);
      return setErrorMessage('Selecione pelo menos um Perfil');
    }

    await _groupUserService.saveEmployee(newUserData).then((result: any) => {
      if (!result?.response?.status) {
        dispatch({
          type: 'HIDE_TITLE',
        });
        setSuccess(true);
      } else {
        setErrorMessage(
          result.response.data?.message || result.response.data?.data || 'Ocorreu um erro durante a requisição.'
        );
        setNewUserData({
          ...newUserData,
          profileType: sliderUser?.profileType,
        });
        setLoading(false);
      }
    });
  }

  function onSelectProfile(data: any) {
    setNewUserData({
      ...newUserData,
      profileType: [...newUserData.profileType, data],
    });
  }

  function onRemoveProfile(data: any) {
    setNewUserData({
      ...newUserData,
      profileType: [
        ...newUserData.profileType?.filter((item: any) => item !== data),
      ],
    });
  }

  function setManagerData(data: any) {
    setShowAutoCompleteManager(false);
    setNewUserData({
      ...newUserData,
      manager: data,
      managerId: data.id,
      managerEmail: data.email
    });
  }

  function setCustomerData(data: any) {
    setShowAutoComplete(false);
    setNewUserData({
      ...newUserData,
      email: data.email ?? '',
      username: data.username ?? '',
      fullName: `${data.firstName} ${data.lastName}`,
    });

    setAutoCompleteSelected(true);
  }

  function onChangeCompanyNameInput(e: any) {
    setNewUserData({ ...newUserData, username: e.target.value });

    setAutoCompleteSelected(false);
  }

  function setSellerData(data: any) {
    setShowAutoCompleteSeller(false);
    setNewUserData({
      ...newUserData,
      seller: data,
      sellerId: data.id,
    });
  }

  function onChangeSellerNameInput(e: any) {
    if (e.target.value.length === 0) {
      setNewUserData({ ...newUserData, seller: null, sellerId: null });
    } else {
      setNewUserData({ ...newUserData, seller: { name: e.target.value } });
    }
  }

  function onChangeManagerInput(e: any) {
    if (e.target.value.length === 0) {
      setNewUserData({ ...newUserData, manager: null, managerId: null, managerEmail: null });
    } else {
      setNewUserData({ ...newUserData, manager: { email: e.target.value } });
    }
  }

  async function confirmDeleteUser() {
    if (user?.id === newUserData?.id) {
      toast.info('Atenção', 'Você não pode excluir o usuário logado.');
      return;
    }

    toast.yesNoProtocol(
      'Atenção',
      `Você deseja realmente excluir o usuário ${newUserData.fullName}?`,
      () => deleteUser(),
      'Sim',
      'Não');
  }

  async function deleteUser() {
    setLoadingDelete(true);
    setErrorMessage('');

    try {
      const result = await _groupUserService.saveEmployee({
        ...newUserData,
        deleted: true,
        enabled: false,
      });
      if (result?.response?.status) {
        setErrorMessage(result.response.data?.message || 'Ocorreu um erro durante a requisição.');
        setLoadingDelete(false);
        dispatch({ type: 'CLOSE_TOAST' });
        return;
      }

      dispatch({
        type: 'HIDE_TITLE',
      });
      setSuccess(true);
    } catch (err) {
      setSuccess(false);
    }

    dispatch({ type: 'CLOSE_TOAST' });
  }

  function setBtnRemoveUser() {
    if (!newUserData.id) return;

    return (
      <SwitchComponent roles={['administrator']} type={['employee']}>
        <Row className="mt-3 align-center justify-center">
          <Button
            onClick={() => confirmDeleteUser()}
            spinnerColorClass="color-danger"
            isRequesting={loadingDelete}
            className="outline border-danger bg-danger flex-sm-1 align-center justify-center d-flex px-3"
          >
            <Text className="color-danger font-bold">Remover usuário</Text>
          </Button>
        </Row>
      </SwitchComponent>
    );
  }

  return !success ? (
    <Form id="employeeForm" onValidate={registerUser}>

      <Visibility visible={!loadedSliderData}>
        <Loading size={40} />
      </Visibility>

      <Visibility visible={loadedSliderData}>
        <Column className="mt-3 flex-1">
          <Column>
            <Text className="color-blue-gray mb-2">Usuário de Rede</Text>
            <AutoComplete
              show={showAutoComplete}
              onSelect={(data: any) => setCustomerData(data)}
              request={(value: any) =>
                _groupUserService.keycloakFind(value.toLowerCase())
              }
              content={AutoCompleteKeycloakUserModel}
              maxResults={5}
            >
              <Input
                onChange={onChangeCompanyNameInput}
                onFocus={() => setShowAutoComplete(true)}
                required
                name="Usuário de Rede"
                className="w-100"
                type="text"
                placeholder="Usuário de Rede"
                value={newUserData.username}
                disabled={disabledUserName}
              />
            </AutoComplete>
          </Column>
          <Column className="mt-3">
            <Text className="color-blue-gray mb-2">Nome</Text>
            <Input
              value={newUserData.fullName}
              className="w-100"
              placeholder="escolha um usuário de rede"
              type="text"
              required
              minLength={3}
              maxLength={200}
              name="Funcionário"
              disabled
            />
          </Column>
          <Column className="mt-3">
            <Text className="color-blue-gray mb-2">Email</Text>
            <Input
              value={newUserData.email}
              className="w-100"
              placeholder="escolha um usuário de rede"
              type="text"
              required
              minLength={3}
              maxLength={200}
              name="Email"
              disabled
            />
          </Column>
          <Column className="mt-3">
            <Text className="color-blue-gray mb-2">Perfil</Text>
            <Validator
              value={newUserData.profileType?.[0]}
              name="Perfil"
              required
            >
              <Row className="flex-wrap">
                {userProfileTypes.map((el, index) => (
                  <Row key={index} className="w-50 pb-1">
                    <input
                      id={`${index}cbPT`}
                      type="checkbox"
                      value={el.key}
                      onClick={() =>
                        newUserData.profileType?.find(
                          (item: any) => item === el.key
                        )
                          ? onRemoveProfile(el.key)
                          : onSelectProfile(el.key)
                      }
                      defaultChecked={newUserData?.profileType?.find(
                        (item: any) => item === el.key
                      )}
                    />
                    <label htmlFor={`${index}cbPT`}>
                      <Text className="ml-2 color-blue-gray">{el.value}</Text>
                    </label>
                  </Row>
                ))}
              </Row>
            </Validator>
          </Column>
          <Column>
            <Text className="color-blue-gray mt-3">Vendedor</Text>
            <AutoComplete
              show={showAutoCompleteSeller}
              onSelect={(data: any) => setSellerData(data)}
              request={(value: any) =>
                _groupUserService.sellerAutoComplete(value.toLowerCase())
              }
              content={AutoCompleteSellerModel}
              maxResults={5}
            >
              <Input
                onChange={onChangeSellerNameInput}
                onFocus={() => setShowAutoCompleteSeller(true)}
                name="Vendedor"
                className="w-100"
                type="text"
                placeholder="Vendedor"
                value={newUserData.seller?.name}
              />
            </AutoComplete>
          </Column>
          <Column className="mt-3">
            <Text className="color-blue-gray mb-2">Nível de acesso</Text>
            <Row>
              <label
                htmlFor="radio_administrator"
                className="mr-2 align-center d-flex cursor-pointer"
              >
                <Input
                  type="radio"
                  id="radio_administrator"
                  name="Nível de acesso"
                  value="administrator"
                  required
                  onChange={(e: any) =>
                    setNewUserData({ ...newUserData, roles: [e.target.value] })
                  }
                  checked={
                    newUserData.roles &&
                    newUserData.roles[0] === 'administrator'
                  }
                />
                <Text className="ml-2 color-blue-gray" size={14}>
                  Administrador
                </Text>
              </label>
              <label
                htmlFor="radio_colaborator"
                className="mr-2 align-center d-flex cursor-pointer"
              >
                <Input
                  type="radio"
                  id="radio_colaborator"
                  name="Nível de acesso"
                  value="member"
                  required
                  onChange={(e: any) =>
                    setNewUserData({ ...newUserData, roles: [e.target.value] })
                  }
                  checked={
                    newUserData.roles && newUserData.roles[0] === 'member'
                  }
                />
                <Text className="ml-2 color-blue-gray" size={14}>
                  Colaborador
                </Text>
              </label>
            </Row>
          </Column>
          <Column className="mt-3">
            <Text className="color-blue-gray mb-2">Gerente</Text>
            <AutoComplete
              show={showAutoCompleteManager}
              onSelect={(data: any) => setManagerData(data)}
              request={(value: any) =>
                _groupUserService.autoComplete(value.toLowerCase())
              }
              content={AutoCompleteManagerModel}
              maxResults={5}
            >
              <Input
                onChange={onChangeManagerInput}
                onFocus={() => setShowAutoCompleteManager(true)}
                name="Gerente"
                className="w-100"
                type="text"
                placeholder="Gerente"
                value={newUserData.manager?.email}
              />
            </AutoComplete>
          </Column>
          {errorMessage && (
            <Row className="mt-3">
              <Text size={12} className="animated fadeIn mt-1 color-danger">
                {errorMessage}
              </Text>
            </Row>
          )}
          <>
            <Row className="mt-3 align-center justify-center">
              <Button
                form="employeeForm"
                spinnerColorClass="color-primary"
                isRequesting={loading}
                className="outline bg-primary flex-sm-1 align-center justify-center d-flex px-3"
              >
                <Text className="color-primary font-bold">
                  {textButtonSave}
                </Text>
              </Button>
            </Row>

            {setBtnRemoveUser()}
          </>
        </Column>
      </Visibility>
    </Form>
  ) : (
    <Column className="w-100 h-100 align-center justify-center">
      <img src={IconSuccess} alt="" />
      <Text className="mt-2 color-blue-gray font-bold">
        {textSuccess} com sucesso
      </Text>
      {novo ?? (
        <Text className="mt-3 color-blue-gray" size={12}>
          O usuário receberá por e-mail as boas vindas
        </Text>
      )}
      <Button onClick={close} className="outline mt-4" width="auto">
        <Text className="color-primary font-bold px-3">Fechar</Text>
      </Button>
    </Column>
  );
};

export default AddEditEmployee;
